'use client';

import { useCallback, useSyncExternalStore } from 'react';
import Cookies from 'js-cookie';
import { useSignupCtas } from '@/components/utils/SignupModeProvider';
import { useCtaLinkProps, type CtaLinkProps } from '@/components/utils/useCtaLinkProps';
import { sendGtmEvent } from '@/lib/gtm/gtm';
import { LOGGED_IN_COOKIE } from '@/lib/loggedInCookie';

/**
 * Chi sta leggendo: uno che non ci conosce, o un cliente?
 *
 * La landing non ha sessione. Lo sa da un cookie marcatore che l'app scrive
 * sul dominio condiviso quando l'utente è dentro (`trovanorma_logged_in=1`,
 * non HttpOnly, cancellato al logout): non porta dati, dice solo «ha un
 * account». Basta a non proporre la prova gratuita a chi paga già, e a
 * mandarlo dritto al quesito. Contratto in `docs/news.md` §5.
 */
const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? '';

const subscribe = () => () => {};
const readCookie = () => Cookies.get(LOGGED_IN_COOKIE) === '1';
// Nell'HTML statico nessuno è loggato: la pagina esce con le CTA di vendita
const readOnServer = () => false;

export function useIsLoggedIn(): boolean {
  return useSyncExternalStore(subscribe, readCookie, readOnServer);
}

/**
 * Apre l'app su una nuova conversazione col quesito **precompilato, non
 * inviato** (un quesito costa: lo manda l'utente). `from` dice all'app da
 * quale articolo e da quale riquadro arriva: `news:<slug>:<posizione>`.
 */
export function appQuestionUrl(question: string, from: string): string {
  const params = new URLSearchParams({ q: question, from });
  return `${APP_URL}/?${params.toString()}`;
}

/** L'articolo dentro l'app, con le fonti che si aprono come nei quesiti. */
export const appArticleUrl = (slug: string) => `${APP_URL}/news/${slug}`;

/** Il quesito scritto sulla landing, per chi poi si registra dalla stessa scheda. */
const QUESTION_STORAGE_KEY = 'tn_news_question';

export type AskPosition = 'sidebar' | 'end' | 'sticky' | 'dock' | 'index';

export interface AskAction extends CtaLinkProps {
  label: string;
  /** Riga di rassicurazione sotto il bottone; null per chi è già cliente */
  note: string | null;
  isLoggedIn: boolean;
}

/**
 * La CTA di un articolo. Per chi ha un account porta al quesito nell'app; per
 * tutti gli altri è la CTA del sito (`useSignupCtas`: demo o prova le decide
 * il backend), con la sorgente `news:<slug>:<posizione>` sull'evento della
 * demo e un evento GTM suo, per sapere quale articolo e quale riquadro
 * convertono.
 */
export function useAskAction(slug: string, position: AskPosition) {
  const isLoggedIn = useIsLoggedIn();
  const { primary, note, hasTrial } = useSignupCtas();
  const source = `news:${slug}:${position}`;
  const ctaProps = useCtaLinkProps(source);

  return useCallback(
    (question: string | null, kind: 'suggested' | 'custom' | 'generic' = 'generic'): AskAction => {
      const track = () => {
        sendGtmEvent({
          event: 'news_cta_click',
          news_slug: slug,
          news_cta_position: position,
          news_question_kind: kind,
          news_visitor: isLoggedIn ? 'customer' : 'prospect',
        });
        if (question) {
          try {
            window.sessionStorage.setItem(QUESTION_STORAGE_KEY, question);
          } catch {
            // Storage negato (navigazione privata): il quesito viaggia comunque nell'URL
          }
        }
      };

      if (isLoggedIn) {
        return {
          label: 'Chiedilo a Trovanorma',
          href: question ? appQuestionUrl(question, source) : appArticleUrl(slug),
          onClick: track,
          note: null,
          isLoggedIn,
        };
      }

      const base = ctaProps(primary);
      // La prova porta all'app: il quesito la segue nell'URL, così l'app può
      // riproporlo alla prima conversazione. La demo apre il modal e resta qui.
      const href =
        hasTrial && question && !primary.opensDemoModal
          ? `${base.href}?${new URLSearchParams({ q: question, from: source })}`
          : base.href;
      return {
        label: question && hasTrial ? 'Chiedilo gratis a Trovanorma' : primary.label,
        href,
        onClick: event => {
          track();
          base.onClick?.(event);
        },
        note,
        isLoggedIn,
      };
    },
    [ctaProps, hasTrial, isLoggedIn, note, position, primary, slug, source]
  );
}
